import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { apiService } from '../services/api';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Box,
  Alert,
  Paper
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import ServiceCard from './ServiceCard';
import SafetyLetterNotification from './SafetyLetterNotification';
import ExpenseDashboardSection from './ExpenseDashboardSection';

const SERVICE_INTERVALS = {
  OIL_CHANGE: {
    miles: 5000,
    years: 0.417,
    description: 'Oil change required every 5,000 miles or 5 months'
  },
  ENGINE: {
    miles: 12000,
    years: 1,
    description: 'Service required every 12,000 miles or annually'
  },
  TRANSMISSION: {
    miles: 30000,
    years: 2,
    description: 'Service required every 30,000 miles or every 2 years'
  }
};

const UserDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [remainingTime, setRemainingTime] = useState(900); // 15 minutes in seconds
  const [dashboardData, setDashboardData] = useState({
    latestChecklist: null,
    historicalChecklists: [],
    upcomingMaintenance: [],
    currentExpenseReport: null,
    bankStatement: null
  });
  const [safetyLetter, setSafetyLetter] = useState(null);
  const [safetyLetterLoading, setSafetyLetterLoading] = useState(true);
  const [safetyLetterError, setSafetyLetterError] = useState(null);
  const [vehicleConflict, setVehicleConflict] = useState(null);
  const { user } = useAuth();

  const fetchVehicleConflictStatus = async () => {
    try {
      const response = await apiService.getVehicleConflictStatus();
      if (response?.hasConflict) {
        setVehicleConflict(response);
      }
    } catch (err) {
      console.error('Error fetching vehicle conflict status:', err);
      // Don't show error to user, just log it
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      let dashboardResponse = { latestChecklist: null, historicalChecklists: [], upcomingMaintenance: [] };
      let expenseReportResponse = null;
      let bankStatementResponse = null;

      try {
        dashboardResponse = await apiService.getUserDashboard(user.id);
      } catch (error) {
        console.error('Error fetching main dashboard data:', error);
      }

      try {
        // Get the current month and year
        const today = new Date();
        const currentMonth = today.getMonth() + 1;
        const currentYear = today.getFullYear();
        
        expenseReportResponse = await apiService.getOrCreateMonthlyReport({
          month: currentMonth,
          year: currentYear
        });
        
        // Get bank statement for the current month/year
        bankStatementResponse = await apiService.getUserBankStatement(currentMonth, currentYear);
      } catch (error) {
        console.error('Error fetching expense report data:', error);
      }

      setDashboardData({
        latestChecklist: dashboardResponse.latestChecklist || null,
        historicalChecklists: dashboardResponse.recentChecklists || [],
        upcomingMaintenance: dashboardResponse.upcomingMaintenance || [],
        currentExpenseReport: expenseReportResponse?.report || null,
        bankStatement: bankStatementResponse || null
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to fetch dashboard data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSafetyLetter = async () => {
    try {
      setSafetyLetterLoading(true);
      setSafetyLetterError(null);
      const response = await apiService.getCurrentMonthSafetyLetter();
      console.log('Safety letter response:', response);
      setSafetyLetter(response);
    } catch (err) {
      console.error('Error fetching safety letter:', err);
      setSafetyLetterError('Failed to fetch safety letter.');
    } finally {
      setSafetyLetterLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      Promise.all([
        fetchDashboardData(),
        fetchSafetyLetter(),
        fetchVehicleConflictStatus()
      ]);
    }
  }, [user]);

  const handleSafetyLetterSign = async () => {
    await fetchSafetyLetter();
    await fetchDashboardData();
  };

  if (loading || safetyLetterLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const { latestChecklist, historicalChecklists, currentExpenseReport, bankStatement } = dashboardData;

  // Utility function to calculate next service date based on average monthly mileage
  const calculateNextServiceDate = (lastServiceDate, serviceMileageInterval, serviceYearInterval, serviceType) => {
    if (!lastServiceDate) return null;
    
    const lastDate = new Date(lastServiceDate);
    
    // Default: Add the appropriate years to last service date
    const timeBasedNextDate = new Date(lastDate);
    timeBasedNextDate.setFullYear(timeBasedNextDate.getFullYear() + serviceYearInterval);
    
    // Try to calculate based on mileage if we have the data
    if (historicalChecklists?.length >= 2) {
      // Find the checklist that contains the last service date
      const relevantChecklist = historicalChecklists.find(c => 
        new Date(c.date).toISOString().split('T')[0] === new Date(lastServiceDate).toISOString().split('T')[0]
      );
      
      if (relevantChecklist && historicalChecklists[0]) {
        const current = historicalChecklists[0]; // Most recent checklist
        
        // Calculate miles driven since last service
        const milesTraveled = current.current_odometer - relevantChecklist.current_odometer;
        
        // If we haven't hit the mileage interval yet, calculate when we will
        if (milesTraveled < serviceMileageInterval) {
          const milesRemaining = serviceMileageInterval - milesTraveled;
          
          // Calculate average monthly mileage based on all checklists
          let totalMilesTraveled = 0;
          historicalChecklists.forEach(checklist => {
            if (typeof checklist.miles_traveled === 'number' && checklist.miles_traveled > 0) {
              totalMilesTraveled += checklist.miles_traveled;
            }
          });
          
          // Sort checklists by date (oldest first)
          const sortedChecklists = [...historicalChecklists].sort((a, b) => 
            new Date(a.date) - new Date(b.date)
          );
          
          // Calculate time span in months
          const oldestDate = new Date(sortedChecklists[0].date);
          const newestDate = new Date(sortedChecklists[sortedChecklists.length - 1].date);
          
          // Calculate months difference
          const monthDiff = (
            (newestDate.getFullYear() - oldestDate.getFullYear()) * 12 + 
            (newestDate.getMonth() - oldestDate.getMonth()) +
            (newestDate.getDate() - oldestDate.getDate()) / 30
          );
          
          // Calculate average monthly mileage
          const totalMonths = Math.max(0.5, monthDiff);
          let milesPerMonth;
          
          if (totalMonths < 1) {
            // For very short periods, convert to monthly equivalent
            const daysDiff = Math.max(1, Math.floor((newestDate - oldestDate) / (1000 * 60 * 60 * 24)));
            milesPerMonth = (totalMilesTraveled / daysDiff) * 30;
          } else {
            milesPerMonth = totalMilesTraveled / totalMonths;
          }
          
          // Apply reasonable limits based on service type
          if (milesPerMonth <= 0) {
            milesPerMonth = 600; // Default 600 miles/month
          } else if (milesPerMonth > 7500) {
            milesPerMonth = 7500; // Cap at 7,500 miles/month
          }
          
          // Calculate months until next service
          const monthsUntilNextService = milesRemaining / milesPerMonth;
          
          // Convert to days (approximate)
          const daysUntilNextService = Math.ceil(monthsUntilNextService * 30);
          
          // Cap maximum projection based on service type
          // For engine service: max 1 year, for transmission: max 2 years
          const maxDaysUntilService = serviceYearInterval * 365;
          const adjustedDaysUntilService = Math.min(daysUntilNextService, maxDaysUntilService);
          
          const mileageBasedNextDate = new Date();
          mileageBasedNextDate.setDate(mileageBasedNextDate.getDate() + adjustedDaysUntilService);
          
          console.log(`${serviceType} calculation:`, {
            milesPerMonth,
            milesRemaining,
            monthsUntilNextService,
            daysUntilNextService,
            adjustedDaysUntilService,
            mileageBasedDate: mileageBasedNextDate.toLocaleDateString(),
            timeBasedDate: timeBasedNextDate.toLocaleDateString(),
            usingMileageBased: mileageBasedNextDate < timeBasedNextDate
          });
          
          // Return the earlier of time-based or mileage-based date
          return mileageBasedNextDate < timeBasedNextDate ? 
            mileageBasedNextDate.toISOString() : 
            timeBasedNextDate.toISOString();
        } else {
          // If we've already exceeded the mileage interval, the service is due now
          return new Date().toISOString();
        }
      }
    }
    
    // If we couldn't calculate based on miles, fall back to time-based estimate
    return timeBasedNextDate.toISOString();
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Vehicle Maintenance Dashboard
      </Typography>

      {/* Vehicle Conflict Warning */}
      {vehicleConflict && vehicleConflict.hasConflict && (
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 3, 
            '& .MuiAlert-message': { width: '100%' }
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Vehicle Assignment Conflict
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Your vehicle unit number (<strong>{vehicleConflict.vehicleNumber}</strong>) is currently also assigned to <strong>{vehicleConflict.conflictUsername}</strong>.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Please speak to your service manager to correct this issue. 
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
            Note: You can complete safety letters and expense reports, but vehicle inspections and maintenance invoices are blocked until this is resolved.
          </Typography>
        </Alert>
      )}

      {/* Safety Letter Section */}
      {safetyLetterError ? (
        <Alert severity="error" sx={{ mb: 3 }}>{safetyLetterError}</Alert>
      ) : safetyLetter && !safetyLetter.has_signed ? (
        <Paper sx={{ p: 3, mb: 3, bgcolor: '#f5f5f5' }}>
          <Typography variant="h6" gutterBottom>
            Monthly Safety Letter
          </Typography>
          <SafetyLetterNotification 
            letter={safetyLetter} 
            onSign={handleSafetyLetterSign}
          />
        </Paper>
      ) : safetyLetter && safetyLetter.has_signed ? (
        <Alert severity="success" sx={{ mb: 3 }}>
          You have signed the safety letter for {new Date(safetyLetter.year, safetyLetter.month - 1).toLocaleString('default', { 
            month: 'long',
            year: 'numeric'
          })}
        </Alert>
      ) : null}

      <Grid container spacing={3}>
        {/* Expense Report Section */}
        <Grid item xs={12} md={6}>
          <ExpenseDashboardSection 
            report={currentExpenseReport} 
            bankStatement={bankStatement}
          />
        </Grid>

        {/* Service Cards */}
        <Grid item xs={12} md={6}>
          <ServiceCard 
            title="Oil Change"
            lastServiceDate={
              latestChecklist?.oil_change_next_date || 
              (latestChecklist?.oil_change_last_date ? 
                (() => {
                  // Calculate next date based on last oil change date and miles remaining until next service
                  const lastDate = new Date(latestChecklist.oil_change_last_date);
                  
                  // Default: Add 5 months to last oil change date
                  const timeBasedNextDate = new Date(lastDate);
                  timeBasedNextDate.setMonth(timeBasedNextDate.getMonth() + Math.round(SERVICE_INTERVALS.OIL_CHANGE.years * 12));
                  
                  // Try to calculate based on mileage if we have the data
                  if (historicalChecklists?.length >= 2) {
                    const current = historicalChecklists[0];
                    // Find the checklist that contains the last oil change date
                    const relevantChecklist = historicalChecklists.find(c => 
                      new Date(c.date).toISOString().split('T')[0] === new Date(latestChecklist.oil_change_last_date).toISOString().split('T')[0]
                    );
                    
                    if (relevantChecklist && current) {
                      // Calculate miles driven since last oil change
                      const milesTraveled = current.current_odometer - relevantChecklist.current_odometer;
                      
                      // If we haven't hit 5,000 miles yet, calculate when we will
                      if (milesTraveled < SERVICE_INTERVALS.OIL_CHANGE.miles) {
                        const milesRemaining = SERVICE_INTERVALS.OIL_CHANGE.miles - milesTraveled;
                        
                        // Use a more adaptive approach to calculate miles per day
                        let milesPerMonth;
                        
                        // First, check if we have recent checklists (within last 30 days)
                        // This helps adjust to someone whose driving pattern has changed
                        const recentChecklists = historicalChecklists.filter(c => {
                          const checklistDate = new Date(c.date);
                          const daysSince = Math.floor((new Date() - checklistDate) / (1000 * 60 * 60 * 24));
                          return daysSince <= 30; // Last 30 days
                        });
                        
                        if (recentChecklists.length >= 2) {
                          // Calculate miles per day based on most recent driving pattern
                          const oldestRecent = recentChecklists[recentChecklists.length - 1];
                          const recentMilesTraveled = current.current_odometer - oldestRecent.current_odometer;
                          const recentDays = Math.max(1, Math.floor((new Date() - new Date(oldestRecent.date)) / (1000 * 60 * 60 * 24)));
                          milesPerMonth = recentMilesTraveled / recentDays;
                        } else {
                          // Fall back to overall average since oil change
                          const daysSinceOilChange = Math.max(1, Math.floor((new Date() - lastDate) / (1000 * 60 * 60 * 24)));
                          milesPerMonth = milesTraveled / daysSinceOilChange;
                        }
                        
                        // Apply reasonable limits to avoid extreme projections
                        if (milesPerMonth <= 0) {
                          milesPerMonth = 10; // Minimum assumption of 10 miles/day
                        } else if (milesPerMonth > 250) {
                          milesPerMonth = 250; // Cap at 250 miles/day (high but reasonable)
                        }
                        
                        // Calculate days until next oil change
                        const daysUntilNextChange = Math.ceil(milesRemaining / milesPerMonth);
                        
                        // Cap maximum projection at 1 year
                        const maxDaysUntilChange = 365;
                        const adjustedDaysUntilChange = Math.min(daysUntilNextChange, maxDaysUntilChange);
                        
                        const mileageBasedNextDate = new Date();
                        mileageBasedNextDate.setDate(mileageBasedNextDate.getDate() + adjustedDaysUntilChange);
                        
                        // Log the detailed calculation for debugging
                        console.log('Final oil change calculation:', {
                          milesPerMonth,
                          milesRemaining,
                          monthsUntilNextChange: milesRemaining / milesPerMonth,
                          daysUntilNextChange,
                          adjustedDaysUntilChange,
                          mileageBasedDate: mileageBasedNextDate.toLocaleDateString(),
                          timeBasedDate: timeBasedNextDate.toLocaleDateString(),
                          usingMileageBased: mileageBasedNextDate < timeBasedNextDate
                        });
                        
                        // Return the earlier of time-based or mileage-based date
                        return mileageBasedNextDate < timeBasedNextDate ? 
                          mileageBasedNextDate.toISOString() : 
                          timeBasedNextDate.toISOString();
                      } else {
                        // If we've already exceeded 5,000 miles, the service is due now
                        return new Date().toISOString();
                      }
                    }
                  }
                  
                  // If we couldn't calculate based on miles, fall back to time-based estimate
                  return timeBasedNextDate.toISOString();
                })() 
                : null
              )
            }
            historicalData={historicalChecklists}
            mileageInterval={SERVICE_INTERVALS.OIL_CHANGE.miles}
            yearInterval={SERVICE_INTERVALS.OIL_CHANGE.years}
            description={SERVICE_INTERVALS.OIL_CHANGE.description}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <ServiceCard 
            title="Engine Service"
            lastServiceDate={
              latestChecklist?.engine_tuneup_date && calculateNextServiceDate(
                latestChecklist.engine_tuneup_date,
                SERVICE_INTERVALS.ENGINE.miles,
                SERVICE_INTERVALS.ENGINE.years,
                "Engine Service"
              )
            }
            historicalData={historicalChecklists}
            mileageInterval={SERVICE_INTERVALS.ENGINE.miles}
            yearInterval={SERVICE_INTERVALS.ENGINE.years}
            description={SERVICE_INTERVALS.ENGINE.description}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <ServiceCard 
            title="Transmission Service"
            lastServiceDate={
              latestChecklist?.transmission_service_date && calculateNextServiceDate(
                latestChecklist.transmission_service_date,
                SERVICE_INTERVALS.TRANSMISSION.miles,
                SERVICE_INTERVALS.TRANSMISSION.years,
                "Transmission Service"
              )
            }
            historicalData={historicalChecklists}
            mileageInterval={SERVICE_INTERVALS.TRANSMISSION.miles}
            yearInterval={SERVICE_INTERVALS.TRANSMISSION.years}
            description={SERVICE_INTERVALS.TRANSMISSION.description}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <ServiceCard 
            title="Fire Extinguisher Certification"
            lastServiceDate={latestChecklist?.fire_extinguisher_cert_date}
            yearInterval={1}
            description="Annual certification required"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <ServiceCard 
            title="Eyewash Solution"
            lastServiceDate={latestChecklist?.safety_kit_eye_wash_expiry}
            yearInterval={0.5}
            description="Replace every 6 months"
          />
        </Grid>

        {/* Recent Checklists */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Recent Inspections</Typography>
              {historicalChecklists.length > 0 ? (
                historicalChecklists.map((checklist) => (
                  <Box key={checklist.id} sx={{ mb: 1 }}>
                    <Typography variant="body1">
                      {new Date(checklist.date).toLocaleDateString()}
                      {checklist.vehicle_number && ` - Vehicle: ${checklist.vehicle_number}`}
                      {typeof checklist.current_odometer === 'number' && 
                        ` - Odometer: ${checklist.current_odometer.toLocaleString()} miles`}
                      {typeof checklist.miles_traveled === 'number' && checklist.miles_traveled > 0 && 
                        ` - Trip: ${checklist.miles_traveled.toLocaleString()} miles`}
                      <Button 
                        component={RouterLink}
                        to={`/checklist/${checklist.id}`}
                        size="small"
                        sx={{ ml: 2 }}
                      >
                        View Details
                      </Button>
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body1" color="textSecondary">
                  No recent inspections found.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Button */}
      <Box mt={3}>
        <Button 
          component={RouterLink} 
          to="/new-checklist" 
          variant="contained" 
          color="primary"
          size="large"
          disabled={vehicleConflict?.hasConflict}
          title={vehicleConflict?.hasConflict ? 'Vehicle inspections are blocked until the vehicle conflict is resolved' : ''}
        >
          Create New Inspection
        </Button>
        {vehicleConflict?.hasConflict && (
          <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
            Vehicle inspections are blocked until the vehicle conflict is resolved. Please contact your service manager.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default UserDashboard;